import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AxiosError } from 'axios';
import useAuthGuard from '../../hooks/useAuthGuard';
import {
  createQuestion,
  deleteQuestion,
  fetchQuestionById,
  fetchQuestionReferenceData,
  updateQuestion,
} from '../../services/questionService';
import { fetchGroupTypes } from '../../services/taxonomyService';
import type {
  DifficultyLevel,
  QuestionDetail,
  QuestionPayload,
  QuestionReferenceData,
  QuestionType,
  Status,
  GroupTypeResource,
  RichContent,
} from '../../types';
import RichTextEditor from '../../components/RichTextEditor';
import {
  TextInput,
  TextArea,
  Select,
  MultiSelect,
  MultiAutocompleteSelect,
  Switch,
} from '../../components/FormComponents';

const QUESTION_TYPES = [
  'MCQ_SINGLE',
  'MCQ_MULTIPLE',
  'STATEMENT',
  'TRUE_FALSE',
  'TYPE_ANSWER',
] as const satisfies readonly QuestionType[];

const DIFFICULTY_LEVELS = [
  'EASY',
  'MEDIUM',
  'HARD',
  'VERY_HARD',
] as const satisfies readonly DifficultyLevel[];

const STATUS_VALUES = ['Active', 'Inactive', 'Draft'] as const satisfies readonly Status[];

const normalizeOptionValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.text) {
      return String(obj.text).trim();
    }
    if (obj.content && typeof obj.content === 'object') {
      const content = obj.content as Record<string, unknown>;
      if (content.text) {
        return String(content.text).trim();
      }
    }
  }

  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
};

// Strips HTML tags down to plain text to evaluate empty rich text values.
const richTextStripper = (value: string): string =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isRichTextEmpty = (value: string | null | undefined): boolean => {
  if (!value) {
    return true;
  }
  // Check if content contains image URLs (before stripping HTML)
  if (value.includes('/uploads/images/')) {
    return false;
  }
  const stripped = richTextStripper(value);
  // Check again after stripping in case the URL is in plain text
  if (stripped.includes('/uploads/images/')) {
    return false;
  }
  return stripped.length === 0;
};

const htmlTagPattern = /<[a-z][^>]*>/i;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Converts legacy plain text payloads into minimal HTML paragraphs for the editor.
const ensureHtmlContent = (value: string | RichContent): string => {
  if (!value) {
    return '';
  }

  let textContent = '';
  let imageContent = '';

  if (typeof value === 'string') {
    // Legacy string format
    textContent = value.trim();
  } else if (value && typeof value === 'object') {
    // New rich content format
    textContent = value.text || '';
    imageContent = value.image || '';
  }

  if (!textContent && !imageContent) {
    return '';
  }

  let html = '';

  if (imageContent) {
    html += `<img src="${imageContent}" alt="Question image" style="max-width: 100%; height: auto; margin-bottom: 8px;" />`;
  }

  if (textContent) {
    if (htmlTagPattern.test(textContent)) {
      html += textContent;
    } else {
      const escaped = escapeHtml(textContent);
      html += `<p>${escaped.replace(/\r?\n/g, '</p><p>')}</p>`;
    }
  }

  return html;
};

const questionFormSchema = z
  .object({
    type: z.enum(QUESTION_TYPES),
    instructions: z.string().optional(),
    content: z
      .string()
      .min(1, 'Question content is required')
      .refine((value) => !isRichTextEmpty(value), 'Question content is required'),
    options: z.array(z.object({ value: z.string() })).max(6, 'A maximum of six options is allowed'),
    correctAnswerSingle: z.string(),
    correctAnswerMultiple: z.array(z.string()),
    correctAnswerText: z.string(),
    correctAnswerBoolean: z.enum(['true', 'false'] as const),
    videoSolution: z.string().trim().url('Invalid video solution URL').or(z.literal('')).optional(),
    solvingTime: z.coerce
      .number({ invalid_type_error: 'Solving time must be a number' })
      .int('Solving time must be an integer')
      .min(0, 'Solving time must be zero or greater'),
    isStatic: z.boolean(),
    showSolution: z.boolean(),
    status: z.enum(STATUS_VALUES),
    difficulty: z.enum(DIFFICULTY_LEVELS),
    groupTypeId: z.string().min(1, 'Group type is required'),
    subjectId: z.string().min(1, 'Subject is required'),
    topicId: z.string().min(1, 'Topic is required'),
    subTopicId: z.string().min(1, 'Sub topic is required'),
    tagIds: z.array(z.string()),
    testSeriesIds: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    const optionValues = data.options
      .map((option) => ensureHtmlContent(option.value))
      .filter((value) => !isRichTextEmpty(value));
    const isMcq = data.type === 'MCQ_SINGLE' || data.type === 'MCQ_MULTIPLE';

    if (isMcq && optionValues.length < 2) {
      ctx.addIssue({
        path: ['options'],
        code: z.ZodIssueCode.custom,
        message: 'At least two options are required for MCQ questions',
      });
    }

    if (data.type === 'MCQ_SINGLE') {
      if (!data.correctAnswerSingle) {
        ctx.addIssue({
          path: ['correctAnswerSingle'],
          code: z.ZodIssueCode.custom,
          message: 'Select the correct answer',
        });
      } else {
        const index = Number(data.correctAnswerSingle);
        if (Number.isNaN(index) || index < 0 || index >= optionValues.length) {
          ctx.addIssue({
            path: ['correctAnswerSingle'],
            code: z.ZodIssueCode.custom,
            message: 'Select a valid option',
          });
        }
      }
    }

    if (data.type === 'MCQ_MULTIPLE') {
      if (!data.correctAnswerMultiple.length) {
        ctx.addIssue({
          path: ['correctAnswerMultiple'],
          code: z.ZodIssueCode.custom,
          message: 'Select at least one correct answer',
        });
      } else {
        const hasInvalid = data.correctAnswerMultiple.some((value) => {
          const index = Number(value);
          return Number.isNaN(index) || index < 0 || index >= optionValues.length;
        });

        if (hasInvalid) {
          ctx.addIssue({
            path: ['correctAnswerMultiple'],
            code: z.ZodIssueCode.custom,
            message: 'Select valid options',
          });
        }
      }
    }

    if (data.type === 'TRUE_FALSE' && !data.correctAnswerBoolean) {
      ctx.addIssue({
        path: ['correctAnswerBoolean'],
        code: z.ZodIssueCode.custom,
        message: 'Select the correct answer',
      });
    }

    if (
      (data.type === 'STATEMENT' || data.type === 'TYPE_ANSWER') &&
      isRichTextEmpty(data.correctAnswerText)
    ) {
      ctx.addIssue({
        path: ['correctAnswerText'],
        code: z.ZodIssueCode.custom,
        message: 'Correct answer is required',
      });
    }
  });

type FormValues = z.infer<typeof questionFormSchema>;

const DEFAULT_VALUES: FormValues = {
  type: 'MCQ_SINGLE',
  instructions: '',
  content: '',
  options: [{ value: '' }, { value: '' }],
  correctAnswerSingle: '',
  correctAnswerMultiple: [],
  correctAnswerText: '',
  correctAnswerBoolean: 'true',
  videoSolution: '',
  solvingTime: 60,
  isStatic: true,
  showSolution: true,
  status: 'Draft',
  difficulty: 'MEDIUM',
  groupTypeId: '',
  subjectId: '',
  topicId: '',
  subTopicId: '',
  tagIds: [],
  testSeriesIds: [],
};

const mapQuestionToFormValues = (question: QuestionDetail): FormValues => {
  const isMcq = question.type === 'MCQ_SINGLE' || question.type === 'MCQ_MULTIPLE';
  const rawOptions = Array.isArray(question.options)
    ? (question.options as unknown[]).map((option) => normalizeOptionValue(option)).filter(Boolean)
    : [];

  const options = rawOptions.map((value) => ({ value: ensureHtmlContent(value) })).slice(0, 6);

  while (options.length < (isMcq ? 2 : 1)) {
    options.push({ value: '' });
  }

  const optionValues = options
    .map((option) => ensureHtmlContent(option.value))
    .filter((value) => !isRichTextEmpty(value));

  let correctAnswerSingle = '';
  let correctAnswerMultiple: string[] = [];
  let correctAnswerText = '';
  let correctAnswerBoolean: 'true' | 'false' = 'true';

  switch (question.type) {
    case 'MCQ_SINGLE': {
      const answerValue = ensureHtmlContent(normalizeOptionValue(question.correctAnswer));
      const index = optionValues.findIndex((value) => value === answerValue);
      if (index >= 0) {
        correctAnswerSingle = index.toString();
      }
      // If the answer doesn't match any option, leave it empty (no text input support)
      break;
    }
    case 'MCQ_MULTIPLE': {
      if (Array.isArray(question.correctAnswer)) {
        const answers = (question.correctAnswer as unknown[])
          .map((entry) => ensureHtmlContent(normalizeOptionValue(entry)))
          .filter((value) => !isRichTextEmpty(value));
        const mapped = answers
          .map((answer) => {
            const index = optionValues.findIndex((value) => value === answer);
            return index >= 0 ? index.toString() : null;
          })
          .filter((value): value is string => value !== null);
        correctAnswerMultiple = Array.from(new Set(mapped));
      }
      break;
    }
    case 'TRUE_FALSE': {
      if (typeof question.correctAnswer === 'boolean') {
        correctAnswerBoolean = question.correctAnswer ? 'true' : 'false';
      } else if (typeof question.correctAnswer === 'string') {
        correctAnswerBoolean = question.correctAnswer.toLowerCase() === 'true' ? 'true' : 'false';
      }
      break;
    }
    default: {
      const textAnswer = ensureHtmlContent(normalizeOptionValue(question.correctAnswer));
      if (!isRichTextEmpty(textAnswer)) {
        correctAnswerText = textAnswer;
      }
    }
  }

  return {
    type: question.type,
    instructions: ensureHtmlContent(question.instructions ?? ''),
    content: ensureHtmlContent(question.content),
    options,
    correctAnswerSingle,
    correctAnswerMultiple,
    correctAnswerText,
    correctAnswerBoolean,
    videoSolution: question.videoSolution ?? '',
    solvingTime: question.solvingTime,
    isStatic: question.isStatic,
    showSolution: question.showSolution,
    status: question.status,
    difficulty: question.difficulty,
    groupTypeId: question.groupType?.id ?? '',
    subjectId: question.subTopic.topic.subject.id,
    topicId: question.subTopic.topic.id,
    subTopicId: question.subTopic.id,
    tagIds: question.tags.map((tag) => tag.id),
    testSeriesIds: question.testSeries.map((series) => series.id),
  } satisfies FormValues;
};

const QuestionFormPage = () => {
  useAuthGuard(['admin', 'super_admin']);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [referenceData, setReferenceData] = useState<QuestionReferenceData | null>(null);
  const [groupTypes, setGroupTypes] = useState<GroupTypeResource[]>([]);
  const [initializing, setInitializing] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(questionFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  });

  const questionType = watch('type');
  const selectedSubjectId = watch('subjectId');
  const selectedTopicId = watch('topicId');
  const selectedSubTopicId = watch('subTopicId');
  const currentOptions = watch('options');

  const isMcq = questionType === 'MCQ_SINGLE' || questionType === 'MCQ_MULTIPLE';

  const availableSubjects = referenceData?.subjects ?? [];
  const availableTopics = useMemo(() => {
    if (!selectedSubjectId) {
      return [];
    }

    return (
      referenceData?.subjects.find((subject) => subject.id === selectedSubjectId)?.topics ?? []
    );
  }, [referenceData, selectedSubjectId]);

  const availableSubTopics = useMemo(() => {
    if (!selectedTopicId) {
      return [];
    }

    return availableTopics.find((topic) => topic.id === selectedTopicId)?.subTopics ?? [];
  }, [availableTopics, selectedTopicId]);

  useEffect(() => {
    if (!availableTopics.some((topic) => topic.id === selectedTopicId)) {
      setValue('topicId', '');
      setValue('subTopicId', '');
    }
  }, [availableTopics, selectedTopicId, setValue]);

  useEffect(() => {
    if (!availableSubTopics.some((subTopic) => subTopic.id === selectedSubTopicId)) {
      setValue('subTopicId', '');
    }
  }, [availableSubTopics, selectedSubTopicId, setValue]);

  const loadFormData = useCallback(
    async (signal: AbortSignal) => {
      try {
        if (signal.aborted) {
          return;
        }

        setInitializing(true);
        setLoadError('');

        const [reference, groupTypes] = await Promise.all([
          fetchQuestionReferenceData(signal),
          fetchGroupTypes({ isActive: true }, signal),
        ]);
        if (signal.aborted) {
          return;
        }

        setReferenceData(reference);
        setGroupTypes(groupTypes);

        if (isEdit && id) {
          const question = await fetchQuestionById(id, signal);
          if (signal.aborted) {
            return;
          }
          const formValues = mapQuestionToFormValues(question);
          reset(formValues);
        } else {
          reset(DEFAULT_VALUES);
        }
      } catch (error) {
        if (signal.aborted) {
          return;
        }
        console.error('Failed to load question data', error);
        const axiosError = error as AxiosError<{ message?: string }>;
        const message = axiosError.response?.data?.message ?? 'Failed to load question data';
        setLoadError(message);
      } finally {
        if (!signal.aborted) {
          setInitializing(false);
        }
      }
    },
    [id, isEdit, reset]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadFormData(controller.signal);
    return () => controller.abort();
  }, [loadFormData]);

  const buildPayload = (values: FormValues): QuestionPayload => {
    const optionValues = values.options
      .map((option) => ensureHtmlContent(option.value ?? ''))
      .filter((value) => !isRichTextEmpty(value))
      .slice(0, 6);

    let correctAnswer: unknown;

    if (values.type === 'MCQ_SINGLE') {
      const index = Number(values.correctAnswerSingle);
      correctAnswer = optionValues[index] ?? '';
    } else if (values.type === 'MCQ_MULTIPLE') {
      const answers = values.correctAnswerMultiple
        .map((value) => optionValues[Number(value)])
        .filter((value): value is string => typeof value === 'string' && Boolean(value));
      correctAnswer = Array.from(new Set(answers));
    } else if (values.type === 'TRUE_FALSE') {
      correctAnswer = values.correctAnswerBoolean === 'true';
    } else {
      correctAnswer = isRichTextEmpty(values.correctAnswerText)
        ? ''
        : ensureHtmlContent(values.correctAnswerText);
    }

    const payload: QuestionPayload = {
      type: values.type,
      instructions: isRichTextEmpty(values.instructions)
        ? null
        : ensureHtmlContent(values.instructions ?? ''),
      content: ensureHtmlContent(values.content),
      options: optionValues.length ? optionValues : undefined,
      correctAnswer,
      videoSolution: values.videoSolution?.trim() ? values.videoSolution.trim() : null,
      solvingTime: values.solvingTime,
      isStatic: values.isStatic,
      showSolution: values.showSolution,
      status: values.status,
      difficulty: values.difficulty,
      groupTypeId: values.groupTypeId,
      subTopicId: values.subTopicId,
      tagIds: values.tagIds,
      testSeriesIds: values.testSeriesIds,
    };

    return payload;
  };

  const onSubmit = async (values: FormValues) => {
    try {
      setSaving(true);
      setSaveError('');

      const payload = buildPayload(values);

      if (isEdit && id) {
        await updateQuestion(id, payload);
      } else {
        await createQuestion(payload);
      }

      navigate('/questions');
    } catch (error) {
      console.error('Failed to save question', error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message ?? 'Failed to save question';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !id) {
      return;
    }

    try {
      setDeleteLoading(true);
      setSaveError('');
      await deleteQuestion(id);
      navigate('/questions');
    } catch (error) {
      console.error('Failed to delete question', error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message ?? 'Failed to delete question';
      setSaveError(message);
    } finally {
      setDeleteLoading(false);
      setConfirmDeleteOpen(false);
    }
  };

  const isFormDisabled = initializing || saving;
  const tags = referenceData?.tags ?? [];
  const testSeries = referenceData?.testSeries ?? [];
  const optionError = errors.options as unknown as { message?: string } | undefined;

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Edit Question' : 'Create Question'}
        </Typography>
        {isEdit && (
          <Button
            color="error"
            variant="outlined"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={isFormDisabled}
          >
            Delete Question
          </Button>
        )}
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      <Card>
        {initializing ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Select
                    name="type"
                    control={control}
                    label="Question Type"
                    disabled={isFormDisabled}
                    options={QUESTION_TYPES.map((type) => ({
                      value: type,
                      label: type.replace(/_/g, ' '),
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Select
                    name="difficulty"
                    control={control}
                    label="Difficulty"
                    disabled={isFormDisabled}
                    options={DIFFICULTY_LEVELS.map((level) => ({
                      value: level,
                      label: level.replace(/_/g, ' '),
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Select
                    name="status"
                    control={control}
                    label="Status"
                    disabled={isFormDisabled}
                    options={STATUS_VALUES.map((status) => ({
                      value: status,
                      label: status,
                    }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        label="Question Content"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={isFormDisabled}
                        error={!!errors.content}
                        helperText={errors.content?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="instructions"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        label="Instructions (optional)"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disabled={isFormDisabled}
                        error={!!errors.instructions}
                        helperText={errors.instructions?.message}
                        minHeight={140}
                      />
                    )}
                  />
                </Grid>

                {isMcq && (
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Options</Typography>
                        <Button
                          startIcon={<AddIcon />}
                          variant="outlined"
                          disabled={isFormDisabled || fields.length >= 6}
                          onClick={() => append({ value: '' })}
                        >
                          Add Option
                        </Button>
                      </Stack>

                      <Stack spacing={2}>
                        {fields.map((field, index) => (
                          <Stack
                            key={field.id}
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Controller
                                name={`options.${index}.value`}
                                control={control}
                                render={({ field: optionField }) => (
                                  <RichTextEditor
                                    label={`Option ${index + 1}`}
                                    value={optionField.value ?? ''}
                                    onChange={optionField.onChange}
                                    onBlur={optionField.onBlur}
                                    disabled={isFormDisabled}
                                    minHeight={120}
                                  />
                                )}
                              />
                            </Box>
                            <IconButton
                              onClick={() => remove(index)}
                              disabled={isFormDisabled || (isMcq && fields.length <= 2)}
                              aria-label={`Remove option ${index + 1}`}
                              sx={{ mt: 3 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        ))}
                      </Stack>
                      {optionError?.message && (
                        <FormHelperText error>{optionError.message}</FormHelperText>
                      )}
                    </Stack>
                  </Grid>
                )}

                {questionType === 'MCQ_SINGLE' && (
                  <Grid item xs={12} md={6}>
                    <Select
                      name="correctAnswerSingle"
                      control={control}
                      label="Correct Answer"
                      disabled={isFormDisabled}
                      options={[
                        { value: '', label: 'Select correct option' },
                        ...currentOptions.map((_, index) => ({
                          value: index.toString(),
                          label: `Option ${index + 1}`,
                        })),
                      ]}
                    />
                  </Grid>
                )}

                {questionType === 'MCQ_MULTIPLE' && (
                  <Grid item xs={12} md={6}>
                    <MultiSelect
                      name="correctAnswerMultiple"
                      control={control}
                      label="Correct Answers"
                      disabled={isFormDisabled}
                      options={[
                        { value: '', label: 'Select correct options' },
                        ...currentOptions.map((_, index) => ({
                          value: index.toString(),
                          label: `Option ${index + 1}`,
                        })),
                      ]}
                    />
                  </Grid>
                )}

                {questionType === 'TRUE_FALSE' && (
                  <Grid item xs={12} md={6}>
                    <Select
                      name="correctAnswerBoolean"
                      control={control}
                      label="Correct Answer"
                      disabled={isFormDisabled}
                      options={[
                        { value: 'true', label: 'True' },
                        { value: 'false', label: 'False' },
                      ]}
                    />
                  </Grid>
                )}

                {questionType === 'STATEMENT' && (
                  <Grid item xs={12}>
                    <TextArea
                      name="correctAnswerText"
                      control={control}
                      label="Correct Answer"
                      disabled={isFormDisabled}
                      rows={4}
                    />
                  </Grid>
                )}

                {questionType === 'TYPE_ANSWER' && (
                  <Grid item xs={12}>
                    <TextArea
                      name="correctAnswerText"
                      control={control}
                      label="Correct Answer"
                      disabled={isFormDisabled}
                      rows={4}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={4}>
                  <Select
                    name="groupTypeId"
                    control={control}
                    label="Group Type"
                    disabled={isFormDisabled}
                    options={[
                      { value: '', label: 'Select group type' },
                      ...groupTypes.map((groupType) => ({
                        value: groupType.id,
                        label: groupType.name,
                      })),
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    name="solvingTime"
                    control={control}
                    label="Solving Time (seconds)"
                    type="number"
                    disabled={isFormDisabled}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextInput
                    name="videoSolution"
                    control={control}
                    label="Video Solution URL"
                    disabled={isFormDisabled}
                    helperText="Optional"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Select
                    name="subjectId"
                    control={control}
                    label="Subject"
                    disabled={isFormDisabled}
                    options={[
                      { value: '', label: 'Select subject' },
                      ...availableSubjects.map((subject) => ({
                        value: subject.id,
                        label: subject.name,
                      })),
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Select
                    name="topicId"
                    control={control}
                    label="Topic"
                    disabled={isFormDisabled || !selectedSubjectId}
                    options={[
                      { value: '', label: 'Select topic' },
                      ...availableTopics.map((topic) => ({
                        value: topic.id,
                        label: topic.name,
                      })),
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Select
                    name="subTopicId"
                    control={control}
                    label="Sub Topic"
                    disabled={isFormDisabled || !selectedTopicId}
                    options={[
                      { value: '', label: 'Select sub topic' },
                      ...availableSubTopics.map((subTopic) => ({
                        value: subTopic.id,
                        label: subTopic.name,
                      })),
                    ]}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MultiAutocompleteSelect
                    name="tagIds"
                    control={control}
                    label="Tags"
                    disabled={isFormDisabled}
                    options={tags.map((tag) => ({ id: tag.id, name: tag.name }))}
                    placeholder="Select tags"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultiAutocompleteSelect
                    name="testSeriesIds"
                    control={control}
                    label="Test Series"
                    disabled={isFormDisabled}
                    options={testSeries.map((series) => ({ id: series.id, name: series.name }))}
                    placeholder="Select test series"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Switch
                    name="isStatic"
                    control={control}
                    label="Static"
                    disabled={isFormDisabled}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Switch
                    name="showSolution"
                    control={control}
                    label="Show Solution"
                    disabled={isFormDisabled}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate('/questions')}
                  disabled={isFormDisabled}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isFormDisabled}>
                  {saving ? 'Saving...' : 'Save Question'}
                </Button>
              </Stack>
            </form>
          </CardContent>
        )}
      </Card>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this question? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuestionFormPage;
